import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { organizationAPI, branchesAPI, locationsAPI, employeesAPI } from '../lib/api';
import {
  Building2, MapPin, Plus, Pencil, Trash2, Users, Building, FolderTree, Upload, Download, Copy, AlertTriangle, ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import { BranchFormDialog } from './organization/BranchFormDialog';
import { LocationFormDialog } from './organization/LocationFormDialog';
import { MoveLocationDialog } from './organization/MoveLocationDialog';
import { BulkImportDialog } from '../components/ui/bulk-import-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Organization() {
  const [structure, setStructure] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const orgL = useOrgLabels();
  const [employees, setEmployees] = useState([]);
  const [branchManagerEmployees, setBranchManagerEmployees] = useState([]);
  const [locationManagerEmployees, setLocationManagerEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogType, setDialogType] = useState(null); // 'branch' | 'location'
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [importBranchesOpen, setImportBranchesOpen] = useState(false);
  const [importLocationsOpen, setImportLocationsOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [moveLocationItem, setMoveLocationItem] = useState(null);

  const duplicateBranch = async (id, name) => {
    setDuplicatingId(id);
    try {
      const res = await branchesAPI.duplicate(id);
      toast.success(`Niederlassung "${res.data.name}" wurde dupliziert`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Duplizieren');
    } finally {
      setDuplicatingId(null);
    }
  };

  const duplicateLocation = async (id) => {
    setDuplicatingId(id);
    try {
      const res = await locationsAPI.duplicate(id);
      toast.success(`Standort "${res.data.name}" wurde dupliziert`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Duplizieren');
    } finally {
      setDuplicatingId(null);
    }
  };

  const exportCsv = async (which, format = 'csv') => {
    const apiSvc = which === 'branches' ? branchesAPI : locationsAPI;
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    const filename = which === 'branches'
      ? `niederlassungen_export.${ext}`
      : `standorte_export.${ext}`;
    const mime = format === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv;charset=utf-8';
    try {
      const res = await apiSvc.exportCsv(format);
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export gestartet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    }
  };

  const [branchForm, setBranchForm] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    bundesland: '',
    phone: '',
    email: '',
    manager_id: '',
    is_active: true,
  });

  const [locationForm, setLocationForm] = useState({
    branch_id: '',
    name: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    phone: '',
    email: '',
    manager_id: '',
    is_active: true,
    // GPS fields
    latitude: '',
    longitude: '',
    gps_radius_meters: 100,
    gps_validation_enabled: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [structureRes, employeesRes] = await Promise.all([
        organizationAPI.getStructure(),
        employeesAPI.getAll({ is_active: true }),
      ]);
      setStructure(structureRes.data);
      const allEmployees = employeesRes.data || [];
      setEmployees(allEmployees);
      setBranchManagerEmployees(allEmployees);
      setLocationManagerEmployees(allEmployees);
      organizationAPI.managerCoverage()
        .then((res) => setCoverage(res.data))
        .catch(() => setCoverage(null));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Organisationsstruktur');
    } finally {
      setLoading(false);
    }
  };

  const openBranchDialog = (branch = null) => {
    if (branch) {
      setEditItem(branch);
      setBranchForm({
        name: branch.name,
        code: branch.code || '',
        address: branch.address || '',
        city: branch.city || '',
        postal_code: branch.postal_code || '',
        country: branch.country || 'Deutschland',
        bundesland: branch.bundesland || '',
        phone: branch.phone || '',
        email: branch.email || '',
        manager_id: branch.manager_id || '',
        is_active: branch.is_active !== false,
      });
    } else {
      setEditItem(null);
      setBranchForm({
        name: '',
        code: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'Deutschland',
        bundesland: '',
        phone: '',
        email: '',
        manager_id: '',
        is_active: true,
      });
    }
    setDialogType('branch');
  };

  const openLocationDialog = (branchId, location = null) => {
    setSelectedBranch(branchId);
    if (location) {
      setEditItem(location);
      setLocationForm({
        branch_id: location.branch_id,
        name: location.name,
        address: location.address || '',
        city: location.city || '',
        postal_code: location.postal_code || '',
        country: location.country || 'Deutschland',
        phone: location.phone || '',
        email: location.email || '',
        manager_id: location.manager_id || '',
        is_active: location.is_active !== false,
        // GPS fields
        latitude: location.latitude || '',
        longitude: location.longitude || '',
        gps_radius_meters: location.gps_radius_meters || 100,
        gps_validation_enabled: location.gps_validation_enabled || false,
      });
    } else {
      setEditItem(null);
      setLocationForm({
        branch_id: branchId,
        name: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'Deutschland',
        phone: '',
        email: '',
        manager_id: '',
        is_active: true,
        // GPS fields
        latitude: '',
        longitude: '',
        gps_radius_meters: 100,
        gps_validation_enabled: false,
      });
    }
    setDialogType('location');
  };

  const saveBranch = async () => {
    if (!branchForm.name) {
      toast.error('Bitte Namen eingeben');
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        await branchesAPI.update(editItem.id, branchForm);
        toast.success('Niederlassung aktualisiert');
      } else {
        await branchesAPI.create(branchForm);
        toast.success('Niederlassung erstellt');
      }
      setDialogType(null);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  const saveLocation = async () => {
    if (!locationForm.name || !locationForm.address || !locationForm.city) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    // Validate GPS fields if enabled
    if (locationForm.gps_validation_enabled) {
      if (!locationForm.latitude || !locationForm.longitude) {
        toast.error('Bitte GPS-Koordinaten eingeben');
        return;
      }
    }

    setSaving(true);
    try {
      // Prepare data with proper types
      const dataToSave = {
        ...locationForm,
        latitude: locationForm.latitude ? parseFloat(locationForm.latitude) : null,
        longitude: locationForm.longitude ? parseFloat(locationForm.longitude) : null,
        gps_radius_meters: parseInt(locationForm.gps_radius_meters) || 100,
      };

      if (editItem) {
        await locationsAPI.update(editItem.id, dataToSave);
        toast.success('Standort aktualisiert');
      } else {
        await locationsAPI.create(dataToSave);
        toast.success('Standort erstellt');
      }
      setDialogType(null);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteBranch = async (id, name) => {
    if (!window.confirm(`Möchten Sie die Niederlassung "${name}" wirklich löschen?`)) return;

    try {
      await branchesAPI.delete(id);
      toast.success('Niederlassung gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const deleteLocation = async (id, name) => {
    if (!window.confirm(`Möchten Sie den Standort "${name}" wirklich löschen?`)) return;

    try {
      await locationsAPI.delete(id);
      toast.success('Standort gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  const covByLoc = Object.fromEntries((coverage?.locations || []).map((l) => [l.id, l.has_manager]));
  const covByBranch = Object.fromEntries((coverage?.branches || []).map((b) => [b.id, b.has_manager]));

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="organization-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Organisation</h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie die Struktur: Firma → {orgL.branches} → {orgL.locations}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  data-testid="export-branches-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {orgL.branches} exportieren
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCsv('branches', 'csv')} data-testid="export-branches-csv">
                  CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportCsv('branches', 'xlsx')} data-testid="export-branches-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  data-testid="export-locations-org-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {orgL.locations} exportieren
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCsv('locations', 'csv')} data-testid="export-locations-org-csv">
                  CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportCsv('locations', 'xlsx')} data-testid="export-locations-org-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setImportLocationsOpen(true)}
              data-testid="import-locations-org-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {orgL.locations} importieren
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setImportBranchesOpen(true)}
              data-testid="import-branches-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {orgL.branches} importieren
            </Button>
            <Button
              className="rounded-full"
              onClick={() => openBranchDialog()}
              data-testid="add-branch-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Neue {orgL.branch}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Building className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{structure?.stats?.total_branches || 0}</p>
                <p className="text-sm text-muted-foreground">{orgL.branches}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <MapPin className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{structure?.stats?.total_locations || 0}</p>
                <p className="text-sm text-muted-foreground">{orgL.locations}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{structure?.stats?.total_employees || 0}</p>
                <p className="text-sm text-muted-foreground">Mitarbeiter</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Warnung: fehlende Leiter */}
        {coverage && coverage.total_missing > 0 && (
          <Card className="border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20" data-testid="missing-manager-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/40 shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200">
                  Fehlende Leiter-Zuordnung
                </p>
                <p className="text-red-700 dark:text-red-300 mt-0.5">
                  {coverage.missing_locations > 0 && `${coverage.missing_locations} ${coverage.missing_locations === 1 ? orgL.location : orgL.locations}`}
                  {coverage.missing_locations > 0 && coverage.missing_branches > 0 && ' und '}
                  {coverage.missing_branches > 0 && `${coverage.missing_branches} ${coverage.missing_branches === 1 ? orgL.branch : orgL.branches}`}
                  {' '}haben keinen hinterlegten Leiter. Urlaubs- und Self-Service-Anträge dieser Ebenen finden keinen Genehmiger und bleiben liegen. Bitte unten bei den markierten Einträgen einen Leiter zuweisen.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Company Header */}
        <Card className="border-border/60 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-xl bg-primary">
                <Building2 className="h-8 w-8 text-primary-foreground" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl">{structure?.company?.name || 'Meine Firma'}</h2>
                <p className="text-muted-foreground">Hauptfirma</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branches & Locations Tree */}
        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-heading flex items-center gap-2">
              <FolderTree className="h-5 w-5 text-primary" />
              Organisationsstruktur
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {structure?.branches?.length > 0 ? (
              <Accordion type="multiple" className="w-full">
                {structure.branches.map((branch, index) => (
                  <AccordionItem key={branch.id} value={branch.id} className="border-b border-border/40">
                    <div className="flex items-center pr-1 sm:pr-4 min-w-0">
                      <AccordionTrigger className="px-2 sm:px-6 py-4 hover:bg-secondary/30 hover:no-underline flex-1 min-w-0">
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                            <Building className="h-5 w-5 text-primary" />
                          </div>
                          <div className="text-left flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <span className="font-medium truncate">{branch.name}</span>
                              {branch.code && (
                                <Badge variant="outline" className="font-mono text-xs">
                                  {branch.code}
                                </Badge>
                              )}
                              {!branch.is_active && (
                                <Badge variant="secondary">Inaktiv</Badge>
                              )}
                              {!branch.bundesland && (
                                <Badge
                                  variant="outline"
                                  className="text-xs gap-1 border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
                                  title="Ohne Bundesland werden Feiertage falsch berechnet (Fallback auf Firmen-Standard/NRW). Bitte Bundesland hinterlegen."
                                  data-testid={`branch-missing-bundesland-${branch.id}`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  Bundesland fehlt
                                </Badge>
                              )}
                              {coverage && covByBranch[branch.id] === false && (
                                <Badge
                                  variant="outline"
                                  className="text-xs gap-1 border-red-400 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30"
                                  title="Kein Niederlassungsleiter hinterlegt — Anträge dieser Ebene finden keinen Genehmiger. Bitte einen Leiter zuweisen."
                                  data-testid={`branch-missing-manager-${branch.id}`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  Kein Leiter
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {branch.locations?.length || 0} {branch.locations?.length === 1 ? orgL.location : orgL.locations}
                              {branch.city && ` • ${branch.city}`}
                              {branch.bundesland && ` • ${branch.bundesland}`}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <div className="flex gap-0 sm:gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 sm:h-8 sm:w-8"
                          onClick={() => openBranchDialog(branch)}
                          data-testid={`edit-branch-${branch.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 sm:h-8 sm:w-8"
                          onClick={() => duplicateBranch(branch.id, branch.name)}
                          disabled={duplicatingId === branch.id}
                          title="Duplizieren"
                          data-testid={`duplicate-branch-${branch.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 sm:h-8 sm:w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteBranch(branch.id, branch.name)}
                          data-testid={`delete-branch-${branch.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <AccordionContent className="px-6 pb-4">
                      <div className="ml-11 space-y-2">
                        {branch.locations?.map((location) => (
                          <div
                            key={location.id}
                            className="flex items-center justify-between gap-2 p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                            data-testid={`location-${location.id}`}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <MapPin className="h-4 w-4 text-accent flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <p className="font-medium truncate">{location.name}</p>
                                  {coverage && covByLoc[location.id] === false && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs gap-1 border-red-400 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30"
                                      title="Kein Standortleiter hinterlegt — Anträge/Urlaub dieses Standorts finden keinen Genehmiger. Bitte einen Leiter zuweisen."
                                      data-testid={`location-missing-manager-${location.id}`}
                                    >
                                      <AlertTriangle className="h-3 w-3" />
                                      Kein Leiter
                                    </Badge>
                                  )}
                                  {location.short_id && (
                                    <Badge variant="outline" className="text-xs font-mono">
                                      {location.short_id}
                                    </Badge>
                                  )}
                                  {location.gps_validation_enabled && (
                                    <Badge variant="secondary" className="text-xs gap-1">
                                      <MapPin className="h-3 w-3" />
                                      GPS
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate">
                                  {location.address}, {location.postal_code} {location.city}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openLocationDialog(branch.id, location)}
                                data-testid={`edit-location-${location.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => duplicateLocation(location.id)}
                                disabled={duplicatingId === location.id}
                                title="Duplizieren"
                                data-testid={`duplicate-location-${location.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setMoveLocationItem(location)}
                                title={`In andere ${orgL.branch} verschieben`}
                                data-testid={`move-location-${location.id}`}
                              >
                                <ArrowRightLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteLocation(location.id, location.name)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                data-testid={`delete-location-${location.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => openLocationDialog(branch.id)}
                          data-testid={`add-location-${branch.id}`}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {orgL.location} hinzufügen
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center py-12">
                <Building className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg">Keine {orgL.branches}</h3>
                <p className="text-muted-foreground mt-1">
                  Erstellen Sie die erste {orgL.branch}
                </p>
                <Button
                  className="mt-4 rounded-full"
                  onClick={() => openBranchDialog()}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {orgL.branch} anlegen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <BranchFormDialog
          open={dialogType === 'branch'} onClose={() => setDialogType(null)}
          editItem={editItem} form={branchForm} setForm={setBranchForm}
          onSave={saveBranch} saving={saving} managerEmployees={branchManagerEmployees}
        />

        <LocationFormDialog
          open={dialogType === 'location'} onClose={() => setDialogType(null)}
          editItem={editItem} form={locationForm} setForm={setLocationForm}
          onSave={saveLocation} saving={saving} managerEmployees={locationManagerEmployees}
        />

        <BulkImportDialog
          open={importBranchesOpen}
          onOpenChange={setImportBranchesOpen}
          title="Niederlassungen importieren"
          downloadTemplate={() => branchesAPI.importTemplate()}
          runImport={(file, dryRun) => branchesAPI.import(file, dryRun)}
          templateFilename="niederlassungen_vorlage.csv"
          onSuccess={loadData}
        />

        <BulkImportDialog
          open={importLocationsOpen}
          onOpenChange={setImportLocationsOpen}
          title="Standorte importieren"
          downloadTemplate={() => locationsAPI.importTemplate()}
          runImport={(file, dryRun) => locationsAPI.import(file, dryRun)}
          templateFilename="standorte_vorlage.csv"
          onSuccess={loadData}
        />

        <MoveLocationDialog
          open={!!moveLocationItem}
          onClose={() => setMoveLocationItem(null)}
          location={moveLocationItem}
          branches={structure?.branches || []}
          orgL={orgL}
          onMoved={loadData}
        />
      </div>
    </DashboardLayout>
  );
}
